import { DEFAULT_CONFIG } from "./constants";
import loadScript from "./loadScript";

/**
 * Get the programming language from the executable cell
 * @returns {string} - The language detected, or "python" if not found
 */
const getLanguage = () => {
  const element = document.querySelector("[data-executable=true]");

  if (element !== null) {
    const dataLanguage = element.getAttribute("data-language");
    if (dataLanguage && dataLanguage !== "") {
      return dataLanguage;
    }
  }

  return "python";
};

/**
 * Generates the configuration for the ThebeLab plugin
 * @param {string | undefined} language - The programming language to use
 * @param {object} existingConfig - The existing configuration to merge with
 * @returns {object} - The merged configuration
 */
const getConfig = (language, existingConfig) => {
  const config = {
    ...DEFAULT_CONFIG,
    ...existingConfig,
    kernelOptions: {
      ...(existingConfig?.kernelOptions && { ...existingConfig.kernelOptions }),
    },
    mathjaxUrl: DEFAULT_CONFIG.mathjaxUrl // Ensure mathjaxUrl is *always* from DEFAULT_CONFIG
  };

  switch (language) {
    case "text/x-c++src":
      config.kernelOptions.name = "xcpp14";
      break;
    case "sagemath":
      config.kernelOptions.name = "sagemath";
      break;
    case "julia":
      config.kernelOptions.name = "julia-1.6";
      break;
    case "octave":
      config.kernelOptions.name = "octave";
      break;
    case "r":
      config.kernelOptions.name = "ir";
      break;
    case "python":
    case "python3":
    default:
      config.kernelOptions.name = "python";
      break;
  }

  return config;
};

const insertThebeStatusHTML = (language) => {
  const body = document.getElementsByTagName("body")[0];
  const statusField = document.createElement("div");
  statusField.innerHTML = `${language.toUpperCase()} Session: NOT STARTED`;
  statusField.setAttribute("class", "thebe-status-field");
  body.append(statusField);
};

const registerThebeStatusEvent = (language) => {
  thebelab.on("status", (e, data) => {
    const statusElement =
      document.getElementsByClassName("thebe-status-field")[0];
    if (!statusElement) return; // If no status field, do nothing

    statusElement.innerHTML = `${language.toUpperCase()} Session: ${data.status.toUpperCase()}`;
    statusElement.setAttribute(
      "class",
      `thebe-status-field thebe-status-${data.status}`
    );

    // if (data.status === "ready") {
    //   setTimeout(() => {
    //     statusElement.remove();
    //   }, 3000);
    // }
  });
};

const removeStatusHtmlIfAny = () => {
  const statusField = document.querySelector(".thebe-status-field");
  if (statusField !== null) {
    statusField.remove();
  }
};

const activateThebelab = async (
  passedConfig,
  detectLanguage = true,
  forceLanguage
) => {
  try {
    const executableCell = document.querySelector("[data-executable=true]");
    if (executableCell === null) return; // If no executable cell(s), do nothing

    // check if any pre block with data-executable=true
    const language = forceLanguage || getLanguage();
    const resolvedConfig = getConfig(
      detectLanguage ? language : undefined,
      passedConfig
    );

    // Load thebe script
    await loadScript("https://cdn.jsdelivr.net/npm/thebe@0.9.3/lib/index.min.js");
    thebelab.bootstrap(resolvedConfig);

    if (detectLanguage) {
      removeStatusHtmlIfAny();
      insertThebeStatusHTML(language);
      registerThebeStatusEvent(language);
    }

    // edge warning
    if (navigator.userAgent.includes("Edge")) {
      // disable eslint warning, this is meant to be obtrusive
      alert(
        "WARNING: You're using an older version of Edge. All code cells on this page will be broken on your browser. Please update to Chromium Edge or use a different browser."
      ); // eslint-disable-line no-alert
    }
  } catch (err) {
    console.error("Error loading thebelab:", err);
  }
};

export default activateThebelab;
